import { Color, Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon, CircleAlertIcon, CircleCheckIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import styled from 'styled-components';

import { View } from 'dashboard/components/PlanUpgradeFlow';
import Tooltip from 'dashboard/components/Tooltip';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Blueprint } from 'dashboard/models/blueprint';
import { AvailableOrganizationPlan } from 'dashboard/models/organization-plan';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import useApiLoader from 'lib/hooks/useApiLoader';

interface UnsupportedSolutionsWarningProps {
  newOrganizationPlan: AvailableOrganizationPlan | null;
  blueprints: Blueprint[];
  loadBlueprints: () => Promise<void>;
  handleNavToNextView: (
    currentView: View,
    newOrganizationPlan: AvailableOrganizationPlan | null,
  ) => void;
  handleNavToPreviousView: (currentView: View) => void;
  onClose: () => void;
}

const UnsupportedSolutionsWarning = ({
  newOrganizationPlan,
  blueprints,
  loadBlueprints,
  handleNavToNextView,
  handleNavToPreviousView,
  onClose,
}: UnsupportedSolutionsWarningProps) => {
  const [hasBlueprintsLoadingError, setHasBlueprintsLoadingError] = useState(false);

  const { loading: isLoadingBlueprints } = useApiLoader({
    loader: loadBlueprints,
    deps: [],
    // No need to showMessage for these errors; we indicate the error naturally in the flow
    onError: () => {
      setHasBlueprintsLoadingError(true);
    },
    onNetworkError: () => {
      setHasBlueprintsLoadingError(true);
    },
  });

  if (!newOrganizationPlan) {
    return (
      <StyledModalContainer>
        <ModalHeader
          prefixIcon={<ArrowLeftIcon size={20} />}
          onPrefixIconClick={() => handleNavToPreviousView(View.UnsupportedSolutionsWarning)}
          onClose={onClose}
        >
          Upgrade plan
        </ModalHeader>
        <ModalBody>
          <Paragraph size='lg' color='primary'>
            Sorry, something went wrong. Please try again later.
          </Paragraph>
        </ModalBody>
      </StyledModalContainer>
    );
  }

  return (
    <StyledModalContainer>
      <ModalHeader
        prefixIcon={<ArrowLeftIcon size={20} />}
        onPrefixIconClick={() => handleNavToPreviousView(View.UnsupportedSolutionsWarning)}
        onClose={onClose}
      >
        Upgrade plan
      </ModalHeader>
      <ModalBody>
        <Paragraph size='lg' color='primary'>
          Since you selected the {newOrganizationPlan?.['display-name']} plan, some of the solutions
          you&apos;ve added to your organization may no longer work once your plan is changed. See
          the list below for details about each solution.
        </Paragraph>
        <Spacer height={4} />
        {isLoadingBlueprints ? (
          <>
            <Paragraph color='secondary'>Loading...</Paragraph>
            <Spacer height={2} />
          </>
        ) : (
          <>
            {hasBlueprintsLoadingError ? (
              <>
                <Paragraph color='secondary'>
                  Oops, we couldn&apos;t load the solutions you&apos;ve added. Please try again.
                </Paragraph>
                <Spacer height={2} />
              </>
            ) : (
              <>
                {blueprints.length === 0 ? (
                  <>
                    <Paragraph color='secondary'>
                      You haven&apos;t added any solutions, so you&apos;re all set to proceed!
                    </Paragraph>
                    <Spacer height={2} />
                  </>
                ) : (
                  <>
                    {blueprints.map((blueprint) => (
                      <BlueprintCard
                        key={`blueprint-supported-${blueprint.id}`}
                        name={blueprint.attributes.name}
                        isSupported={blueprint.attributes['supported-self-serve-plans'].includes(
                          newOrganizationPlan.key,
                        )}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )}
        <Spacer height={4} />
        <Footer>
          <Button
            width='auto'
            onClick={() => {
              handleNavToNextView(View.UnsupportedSolutionsWarning, newOrganizationPlan);
            }}
          >
            Continue
          </Button>
        </Footer>
      </ModalBody>
    </StyledModalContainer>
  );
};

const BlueprintCard = ({ name, isSupported }: { name: string; isSupported: boolean }) => {
  return (
    <BlueprintCardWrapper>
      <div>{name}</div>
      <div>
        {isSupported ? (
          <Tooltip
            tooltip='This solution will continue to work on your new plan'
            hideArrow={false}
            placement='bottom'
          >
            <StatusWrapper>
              <span>Will work!</span>
              <CircleCheckIcon color={Color.CoreGreen800} />
            </StatusWrapper>
          </Tooltip>
        ) : (
          <Tooltip
            tooltip='This solution will be disabled on your new plan'
            hideArrow={false}
            placement='bottom'
          >
            <StatusWrapper>
              <span>Will be disabled</span>
              <CircleAlertIcon color={Color.CoreRed800} />
            </StatusWrapper>
          </Tooltip>
        )}
      </div>
    </BlueprintCardWrapper>
  );
};

const StyledModalContainer = styled(ModalContainer)`
  min-width: 700px;
`;

const BlueprintCardWrapper = styled(Card)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const StatusWrapper = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
  align-items: center;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export default UnsupportedSolutionsWarning;
